import { EstimatedItem, WorkareaItemBase, isEstimatedItem } from "../../domain/workareaItem"
import { FocusedView } from "./ItemsRoot"
import { Action, Dispatch, ItemWorkareaData, ViewItem } from "./itemModel"

export const getRecurringWorkareaData = (workareaId: number, workareas: ItemWorkareaData[]) => {
  const workarea = workareas.find(x => x.id === workareaId)

  const masterWorkareaId = workarea?.master ? workarea.id : workarea?.masterWorkareaId ?? 0
  const isRecurringWorkarea = workarea?.master || typeof workarea?.masterWorkareaId === 'number'
  const childWorkareaIds = isRecurringWorkarea ? workareas.filter(x => x.masterWorkareaId === masterWorkareaId).map(x => x.id) : []

  return {
    isRecurringWorkarea,
    masterWorkareaId,
    childWorkareaIds,
    allWorkareaIds: isRecurringWorkarea
      ? [masterWorkareaId, ...childWorkareaIds]
      : []
  }
}

export const isRecurringDispatchNeeded = (focusedView: FocusedView, workareas: ItemWorkareaData[], focusedWorkareaId?: number, copyChangesToChildWorkareas?: boolean) => {
  if (focusedView === 'workarea-estimated' && focusedWorkareaId !== undefined && copyChangesToChildWorkareas) {
    const recurringData = getRecurringWorkareaData(focusedWorkareaId, workareas)
    if (recurringData.isRecurringWorkarea && recurringData.masterWorkareaId === focusedWorkareaId && recurringData.childWorkareaIds.length > 0) {
      return { isNeeded: true, recurringData }
    }
  }
  return { isNeeded: false }
}

export const getRecurringDispatchEstimatedItems = (recurringWorkareaIds: number[], addedItems: WorkareaItemBase[], serverEstimatedItems: EstimatedItem[]) => {
  return addedItems.filter(x => isEstimatedItem(x) && recurringWorkareaIds.includes(x.workareaId))
    .concat(serverEstimatedItems.filter(x => recurringWorkareaIds.includes(x.workareaId)))
}

export const getRecurringDispatchIfNeeded = (dispatch: Dispatch, focusedView: FocusedView, workareas: ItemWorkareaData[], serverEstimatedItems: EstimatedItem[], addedItems: WorkareaItemBase[], focusedWorkareaId?: number, copyChangesToChildWorkareas?: boolean) => {
  const { isNeeded, recurringData } = isRecurringDispatchNeeded(focusedView, workareas, focusedWorkareaId, copyChangesToChildWorkareas)
  if (isNeeded && recurringData) {
    return updateRecurringWorkareasDispatch(dispatch, recurringData.allWorkareaIds, getRecurringDispatchEstimatedItems(recurringData.allWorkareaIds, addedItems, serverEstimatedItems))
  }
  return dispatch
}

export const updateRecurringWorkareasDispatch = (dispatch: Dispatch, allRelatedWorkareaIds: number[], estimatedItems: WorkareaItemBase[]): Dispatch => (action: Action) => {
  switch (action.type) {
    case 'NAME_CHANGED':
    case 'IS_ADDITIONAL_CHANGED':
    case 'BILLABLE_CHANGED':
    case 'UNSURE_CHANGED':
    case 'TAXABLE_CHANGED':
    case 'QUANTITY_CHANGED':
    case 'CUSTOM_COST_CHANGED':
    case 'CUSTOM_PRICE_CHANGED':
    case 'CUSTOM_TOTAL_PRICE_CHANGED':
    case 'CUSTOM_MARGIN_CHANGED':
    case 'DELETE_ITEM':
    case 'UNDELETE_ITEM':
    case 'SYNC_ITEM_PRICING':
      {
        dispatch(action)

        const item = estimatedItems.find(x => x.guid === action.guid)
        if (item) {
          const matches = estimatedItems.filter(x => isRecurringMatch(item, x))
          matches.forEach(match => {
            dispatch({ ...action, guid: match.guid })
          })
        }
      }
      break;
    case 'ADD_ITEM_FROM_CATALOG':
    case 'ADD_TEMPLATE_FROM_CATALOG':
      allRelatedWorkareaIds.forEach(workareaId => dispatch({ ...action, addItemParams: { ...action.addItemParams, workareaId } }))
      break;
    default:
      dispatch(action)
      break;
  }
}

type MatchableItem = {
  guid: string
  name: string
  catalogItemGuid: string
}

export const isRecurringMatch = (masterItem: MatchableItem, candidate: MatchableItem): boolean => {
  return masterItem.guid !== candidate.guid && masterItem.name === candidate.name && masterItem.catalogItemGuid === candidate.catalogItemGuid
}

export type RecurringWorkareaMatches = {
  matchingWorkareaIds: number[]
  nonMatchingWorkareaIds: number[]
}

export const mutateNonMatchingRecurringItems = (masterWorkareaId: number, childWorkareaIds: number[], viewItems: ViewItem[]): RecurringWorkareaMatches => {
  const masterItems = viewItems.filter(x => x.workareaId === masterWorkareaId && !x.isEmptyRow)
  const masterNonDeleted = masterItems.filter(x => !x.isDeleted)
  const matchingWorkareaIds: number[] = [masterWorkareaId]
  const nonMatchingWorkareaIds: number[] = []

  childWorkareaIds.forEach(childWorkareaId => {
    const childItems = viewItems.filter(x => x.workareaId === childWorkareaId && !x.isEmptyRow)
    childItems.forEach(item => {
      const match = masterItems.find(x => isRecurringMatch(x, item))
      if (match) {
        item.differsFromMasterTask = item.isBillable !== match.isBillable || item.quantity !== match.quantity || item.totalPrice !== match.totalPrice || item.isDeleted !== match.isDeleted
      } else {
        item.differsFromMasterTask = true
      }
    })

    const childNonDeleted = childItems.filter(x => !x.isDeleted)
    if ((childNonDeleted.length !== masterNonDeleted.length) || childNonDeleted.some(x => x.differsFromMasterTask === true)) {
      nonMatchingWorkareaIds.push(childWorkareaId)
    } else {
      matchingWorkareaIds.push(childWorkareaId)
    }
  })

  return { matchingWorkareaIds, nonMatchingWorkareaIds }
}
